import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banknote, LogOut, Receipt, ShieldAlert, Search } from 'lucide-react';
import axios from 'axios';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';

const api = axios.create({ baseURL: '/api/provider-portal' });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('provider_portal_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
api.interceptors.response.use((r) => r, (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('provider_portal_token');
    localStorage.removeItem('provider_portal_account');
    window.location.href = '/provider-portal/login';
  }
  return Promise.reject(err);
});

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return ''; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
// PROVIDER-facing status wording — where their money is, not plan-manager internals.
const LABEL = {
  extract_generated: 'Received', created: 'Received', approved: 'Approved',
  claimed: 'Payment processing', paid: 'Payment processing', provider_paid: 'Paid',
  on_hold: 'On hold', declined: 'Declined', cancelled: 'Cancelled',
};
const TONE = {
  approved: 'bg-blue-100 text-blue-700', claimed: 'bg-indigo-100 text-indigo-700', paid: 'bg-indigo-100 text-indigo-700',
  provider_paid: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700', cancelled: 'bg-red-100 text-red-700', on_hold: 'bg-amber-100 text-amber-700',
};

// Provider dashboard — every invoice they've sent, where it's at, and totals.
export default function ProviderPortalDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    api.get('/overview').then((r) => setData(r.data)).catch(() => setData({ kpis: {}, invoices: [] }));
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('provider_portal_token')) { navigate('/provider-portal/login', { replace: true }); return; }
    api.get('/me').then((r) => setMe(r.data)).catch(() => {});
    load();
  }, [navigate, load]);

  const logout = () => {
    localStorage.removeItem('provider_portal_token');
    localStorage.removeItem('provider_portal_account');
    navigate('/provider-portal/login', { replace: true });
  };

  const k = data?.kpis || {};
  const invoices = (data?.invoices || []).filter((v) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (v.provider_invoice_number || '').toLowerCase().includes(s) || (v.participant || '').toLowerCase().includes(s);
  });

  const Kpi = ({ label, value, tone }) => (
    <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3.5">
      <div className="text-[11px] font-medium text-slate-400">{label}</div>
      <div className={clsx('text-lg font-bold tabular-nums mt-0.5', tone || 'text-slate-800')}>{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow">
              <Banknote size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm">Provider Payment Portal</p>
              {me && <p className="text-xs text-slate-400">{me.provider_name}{me.abn ? ` · ABN ${me.abn}` : ''}</p>}
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-500 transition-colors"><LogOut size={15} /> Logout</button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Bank verification notice — transparency on why a payment may be held */}
        {me && me.bank_verified === false && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-800 flex items-start gap-2">
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            <span>Your bank details were recently changed and are being verified by the plan manager before the next payment run. They may call you to confirm — this protects your payments.</span>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Kpi label="Awaiting payment" value={money(k.outstanding)} tone="text-amber-600" />
          <Kpi label="Paid to you (total)" value={money(k.paid_total)} tone="text-emerald-600" />
          <Kpi label="Paid last 30 days" value={k.paid_30d ?? 0} />
        </div>

        {/* Invoices */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
            <span className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Receipt size={15} className="text-teal-600" /> Your invoices</span>
            <span className="flex-1" />
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice # or participant…"
                className="border border-slate-200 rounded-lg pl-7 pr-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-400 w-48" />
            </div>
          </div>
          {!data ? <p className="text-sm text-slate-400 py-10 text-center">Loading…</p>
            : invoices.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">{q ? 'Nothing matches your search.' : 'No invoices on file yet.'}</p>
              : (
                <div className="divide-y divide-slate-50">
                  {invoices.map((v) => (
                    <div key={v.id} className="px-5 py-3 flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">{v.provider_invoice_number ? `#${v.provider_invoice_number}` : 'Invoice'} · {v.participant}</p>
                        <p className="text-[11px] text-slate-400">
                          Invoiced {fmtD(v.invoice_date)}
                          {v.status === 'provider_paid' && v.updated_at ? ` · paid ${fmtD(v.updated_at)}` : ''}
                        </p>
                      </div>
                      <span className="text-sm font-semibold tabular-nums text-slate-700">{money(v.total_amount)}</span>
                      <span className={clsx('text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap', TONE[v.status] || 'bg-slate-100 text-slate-600')}>{LABEL[v.status] || v.status}</span>
                    </div>
                  ))}
                </div>
              )}
        </div>

        <p className="text-center text-[11px] text-slate-400 pb-6">"Received" means the plan manager has your invoice · "Approved" means it's cleared checks · "Payment processing" means it's in the payment run · "Paid" means the money is on its way to your account.</p>
      </div>
    </div>
  );
}
